import {
    type CaseAttachmentPatch, type CasePatch, CaseWriteKind, type CaseWriteRequest,
} from '@/types/cases';

import { getFailureMessage } from './failureMessage';

type AttachmentField = Exclude<keyof CaseAttachmentPatch, 'position'>;

const CASE_FAILURES: Record<keyof CasePatch, string> = {
    title: 'Could not rename the case',
    status: 'Could not change the case status',
    description: 'Could not save the description',
};

const ATTACHMENT_FAILURES: Record<keyof CaseAttachmentPatch, string> = {
    name: 'Could not rename the attachment',
    note: 'Could not save the note',
    position: 'Could not move the attachment',
};

export const NEW_CASE_KEY = 'new-case';

export function getCaseFieldKey(caseId: string, field: keyof CasePatch): string {
    return `${caseId}:${field}`;
}

export function getAttachmentFieldKey(attachmentId: string, field: AttachmentField): string {
    return `${attachmentId}:${field}`;
}

export function getAttachmentMoveKey(caseId: string): string {
    return `${caseId}:position`;
}

function getPatchFields<P extends object>(patch: P): (keyof P)[] {
    return Object.keys(patch) as (keyof P)[];
}

export function getCaseWriteKeys(request: CaseWriteRequest): string[] {
    switch (request.kind) {
        case CaseWriteKind.Create:
            return [NEW_CASE_KEY];
        case CaseWriteKind.Update:
            return getPatchFields(request.patch)
                .map((field) => getCaseFieldKey(request.caseId, field));
        case CaseWriteKind.AttachmentUpdate:
            return getPatchFields(request.patch).map((field) => (
                field === 'position'
                    ? getAttachmentMoveKey(request.caseId)
                    : getAttachmentFieldKey(request.attachmentId, field)
            ));
        default:
            return request satisfies never;
    }
}

function getPatchFailure<P extends object>(
    patch: P,
    failures: Record<keyof P, string>,
    fallback: string,
): string {
    const fields = getPatchFields(patch);
    const [field] = fields;
    return fields.length === 1 && field !== undefined ? failures[field] : fallback;
}

export function getCaseWriteFailure(request: CaseWriteRequest, error: unknown): string {
    switch (request.kind) {
        case CaseWriteKind.Create:
            return getFailureMessage(error, 'Could not start the case');
        case CaseWriteKind.Update:
            return getFailureMessage(
                error,
                getPatchFailure(request.patch, CASE_FAILURES, 'Could not save the case'),
            );
        case CaseWriteKind.AttachmentUpdate:
            return getFailureMessage(
                error,
                getPatchFailure(
                    request.patch,
                    ATTACHMENT_FAILURES,
                    'Could not save the attachment',
                ),
            );
        default:
            return request satisfies never;
    }
}
